import { ForwardedRef, forwardRef } from "react";
import css from "@styled-system/css";
import styled from "styled-components";
import Box from "../Box/Box";
import Typography from "../Typography/Typography";
import { AnimatedIconContainer } from "../Accordion/AccordionHeader";
import {
  BackgroundColorProps,
  BorderProps,
  variant,
  AlignItemsProps,
  alignItems,
} from "styled-system";

import { stepVariants } from "./variants";
import Icon from "../Icon/Icon";
import { StepHeadingProps, StepVariant } from "./verticalTimeline.types";
import { Highlight } from "./Step";

const TimelineStep = styled.div<{
  variant: StepVariant;
  stepBorderColor?: BorderProps["borderColor"];
  stepBackgroundColor?: BackgroundColorProps["backgroundColor"];
}>`
  width: 30px;
  height: 30px;
  min-height: 30px;
  border-radius: 50%;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 3px solid;
  ${variant({ variants: stepVariants })}
  ${(props) =>
    css({
      mb: 2,
      borderColor: (props.stepBorderColor
        ? props.stepBorderColor
        : "grey-blue") as any,
      ...(props.stepBackgroundColor && {
        backgroundColor: props.stepBackgroundColor as any,
      }),
    })}
`;

export const AdditionalInfo = styled(Typography)`
  display: inline-block;
  font-size: 14px;
`;

const Title = styled.h6`
  display: -webkit-box;
  max-width: 100%;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  ${css({
    lineHeight: `19px`,
    flex: 1,
    color: "grey-blue",
    fontSize: "h6",
    fontWeight: "medium",
    margin: 0,
    marginLeft: 2,
  })}
`;

const Headline = styled.div<AlignItemsProps & { variant: StepVariant }>`
  padding-bottom: 8px;
  display: flex;
  width: 100%;
  min-width: 300px;
  max-height: 38px;
  position: relative;
  ${alignItems}
  ${(props) =>
    css({
      ...(props.variant === "disabled secondary" && { opacity: 0.37 }),
    })}
`;

const StepHeading = forwardRef(
  (
    {
      variant,
      isExpanded = false,
      customIcon,
      stepBorderColor,
      stepBackgroundColor,
      title,
      additionalInfo,
      shouldDisplayTimeline = true,
      accordion,
      numberOfLines,
    }: StepHeadingProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const shouldDisplayIcon = Boolean(variant === "completed" || customIcon);

    return (
      <Box display="flex" height={shouldDisplayTimeline ? 73 : 38}>
        <Box
          display="inline-flex"
          flexDirection="column"
          alignItems="center"
          width="30px"
          css={"position: relative;"}
        >
          <TimelineStep
            variant={variant}
            stepBorderColor={stepBorderColor}
            stepBackgroundColor={stepBackgroundColor}
          >
            {shouldDisplayIcon && (
              <Icon
                name={customIcon?.name || "check"}
                width={customIcon?.width || 14}
                height={customIcon?.height || 14}
                weight={customIcon?.weight || "solid"}
                color={customIcon?.color || "primary-white"}
              />
            )}
          </TimelineStep>

          {shouldDisplayTimeline && <Highlight />}
        </Box>
        <Headline
          alignItems={numberOfLines === 1 ? "center" : "start"}
          title={numberOfLines > 2 ? title : undefined}
          variant={variant}
        >
          <Title ref={ref}>{title}</Title>
          {additionalInfo && (
            <AdditionalInfo
              variant="body"
              color="grey-blue"
              htmlTag="span"
              margin={0}
              marginRight={!accordion ? 41 : 1}
              marginLeft={7}
            >
              {additionalInfo}
            </AdditionalInfo>
          )}
          {accordion && (
            <AnimatedIconContainer
              height="100%"
              position="relative"
              top={numberOfLines === 1 ? "55%" : "40%"}
              marginLeft={!additionalInfo ? 7 : 0}
            >
              <Icon
                className={isExpanded ? "expanded" : "collapsed"}
                aria-hidden={true}
                color="grey-blue"
                name="angle-down"
                width={25}
                height={25}
              />
            </AnimatedIconContainer>
          )}
        </Headline>
      </Box>
    );
  }
);

export default StepHeading;
